import React, { useRef, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Field as AkField } from '@atlaskit/form';
import PriorityTrivialIcon from '@atlaskit/icon/core/priority-trivial';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InlineDialog from '@atlaskit/inline-dialog';
import AkInlineEdit from '@atlaskit/inline-edit';
import AkSelect, {
	type OptionType,
	type SingleValueProps,
	components,
	type OptionProps,
} from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { Row } from '../../../../../../../../../common/ui/styled';
import { toSubGoalPriorityFieldName } from '../../../../../../../../../common/utils/goal/field';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../../../../controllers/enable-sla-hierarchical-goals';
import { useFetchPrioritiesService } from '../../../../../../../../../services/fetch-priorities/index.tsx';
import messages from './messages';

type Props = {
	isEditing: boolean;
	focusOnEdit?: boolean;
	id: string;
	goalId: string;
	metricId?: number;
	selectedPriority: string;
	onEditRequested: () => void;
	onChange?: () => void;
};

const Option = (props: OptionProps) => (
	<components.Option {...props}>
		{props.data.value === 'EMPTY' ? (
			<IconContainer>
				<PriorityTrivialIcon label="" LEGACY_size="small" color="currentColor" />
			</IconContainer>
		) : (
			<ImageIcon src={props.data.icon} alt={props.data.label} />
		)}
		{props.data.label}
	</components.Option>
);

const PrioritiesSelect = ({
	id,
	goalId,
	metricId,
	isEditing,
	onEditRequested,
	focusOnEdit = false,
	selectedPriority,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onChange = () => {},
}: Props) => {
	const [{ values: prioritiesOptions, loading }] = useFetchPrioritiesService();
	const fieldName = toSubGoalPriorityFieldName(goalId, id);
	const akSelectRef = useRef<unknown>(null);

	useEffect(() => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		isEditing && focusOnEdit && akSelectRef.current?.focus();
		// @ts-expect-error - TS2339 - Property 'blur' does not exist on type 'Element'.
		!isEditing && document?.activeElement?.blur();
	}, [focusOnEdit, isEditing]);

	// Priority name having multiple words is always saved in double quotes.
	// Eg: `priority = "High/Medium"
	// And then the selected priority becomes '"High/Medium"' and matching it with the priority options doesnt give us anything
	// since the option is 'High/Medium', with no double quotes wrapped.
	// So, to avoid this, we need to trim those extra double quotes around
	const selectedPriorityTrimmed = selectedPriority?.replace(/^"+|"+$/g, '');
	const defaultValue = useMemo(() => {
		const selectedPriorityOption = prioritiesOptions.find(
			// If the project has `high`, medium` and `low` priorities
			// and `high` is selected. Later on, `high` is renamed to `highest` in priorities list, it breaks the UI
			// To handle this scenario, below code is added.
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(option: any) => option.value === selectedPriorityTrimmed,
		);

		if (selectedPriorityOption) {
			return selectedPriorityOption;
		}

		const isPriorityChanged = !selectedPriorityOption && selectedPriorityTrimmed.length > 0;

		if (isPriorityChanged) {
			return {
				label: selectedPriorityTrimmed,
				value: selectedPriority,
				icon: '',
			};
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const defaultOption = prioritiesOptions.find((option: any) => option.value === 'EMPTY');

		return defaultOption;
	}, [prioritiesOptions, selectedPriorityTrimmed, selectedPriority]);
	const [
		{ isSLAHierarchicalGoalsEnabled, isPriorityNameChanged, prioritiesSelected },
		{ updateIsPriorityNameChanged },
	] = useSLAHierarchicalGoalsEnabled();
	const selectedOption = prioritiesOptions.filter(
		(option) => option.value === selectedPriorityTrimmed,
	);

	// If the project has `high`, medium` and `low` priorities
	// and `high` is selected. Later on, `high` is renamed to `highest` in priorities list, it breaks the UI
	// To handle this scenario, below code is added.
	const isPriorityChanged = selectedOption.length === 0 && selectedPriorityTrimmed.length > 0;
	if (prioritiesOptions.length > 0 && isPriorityNameChanged !== isPriorityChanged)
		updateIsPriorityNameChanged(metricId, goalId, isPriorityChanged);
	// eslint-disable-next-line react/no-unstable-nested-components
	const SingleValue = ({ children, ...props }: SingleValueProps<OptionType>) => (
		<components.SingleValue {...props}>
			<>
				{props.data.value === 'EMPTY' || isPriorityChanged ? (
					<IconContainer>
						<PriorityTrivialIcon LEGACY_size="small" label="" color="currentColor" />
					</IconContainer>
				) : (
					<ImageIcon src={props.data.icon} alt="" />
				)}
				{children}
			</>
		</components.SingleValue>
	);
	const { formatMessage } = useIntl();
	let filteredPriorityOptions = prioritiesOptions;

	// @ts-expect-error - TS2538
	if (prioritiesSelected?.[metricId]?.[goalId]?.length) {
		filteredPriorityOptions = prioritiesOptions.filter(
			// @ts-expect-error - TS2538
			(option) => !prioritiesSelected[metricId][goalId].includes(option.value),
		);
	}

	return (
		<AkField name={fieldName} defaultValue={defaultValue}>
			{({ fieldProps: { value, ...rest } }) => (
				// @ts-expect-error - TS2741 - Property 'defaultValue' is missing in type '{ editView: ({ errorMessage, isInvalid }: ExtendedFieldProps<string>) => Element; readView: () => Element; isEditing: boolean; onConfirm: () => void; onEdit: () => void; hideActionButtons: true; readViewFitContainerWidth: true; }' but required in type 'InlineEditProps<string>'.
				<AkInlineEdit
					editView={({ errorMessage, isInvalid }) => (
						<InlineDialog isOpen={isInvalid} content={<>{errorMessage}</>} placement="right">
							<Row>
								<AkSelect
									// @ts-expect-error - TS2322
									ref={akSelectRef}
									options={filteredPriorityOptions}
									isLoading={loading}
									value={value}
									aria-label={formatMessage(messages.selectPriority)}
									appearance={isSLAHierarchicalGoalsEnabled ? 'subtle' : 'default'}
									// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
									styles={{
										singleValue: (base) => ({
											...base,
											display: 'flex',
											flexGrow: 1,
										}),
										option: (base) => ({
											...base,
											display: 'flex',
											flexGrow: 1,
										}),
										container: (base) => ({
											...base,
											flexGrow: 1,
										}),
									}}
									components={{
										Option,
										SingleValue,
									}}
									{...rest}
									onChange={(...args) => {
										// @ts-expect-error - TS2554 - Expected 1 arguments, but got 2.
										rest.onChange(...args);
										onChange();
									}}
									testId="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.priorities.ak-select"
								/>
								{isPriorityChanged && (
									<Tooltip content={formatMessage(messages.invalidPriority)} position="top">
										<Button
											iconBefore={
												<ErrorIcon
													primaryColor={token('color.icon.danger', colors.R500)}
													size="medium"
													label=""
												/>
											}
											appearance="subtle"
											aria-label={formatMessage(messages.invalidPriority)}
										/>
									</Tooltip>
								)}
							</Row>
						</InlineDialog>
					)}
					isEditing
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onConfirm={() => {}}
					onEdit={onEditRequested}
					hideActionButtons
					readViewFitContainerWidth
				/>
			)}
		</AkField>
	);
};

export default PrioritiesSelect;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageIcon = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: 2 * gridSize,
	marginInlineEnd: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: 2 * gridSize,
	marginInlineEnd: token('space.100', '8px'),
});
